// The React view of the service-worker update arbiter — a Humble Object.
//
// It subscribes and it returns; it decides nothing. Every decision about
// *whether* an update may be applied lives in `lib/pwa/updateApplyPolicy` and
// `lib/pwa/appReload`, which are testable without a service worker, without a
// network, and without React — a necessity given this bug's timescale (hours,
// offline, backgrounded), which no end-to-end run reaches.

import { useSyncExternalStore } from "react";
import {
	getUpdateState,
	requestApplyUpdate,
	subscribeUpdateState,
	type UpdateState,
} from "@/lib/pwa/serviceWorkerClient";

export interface ServiceWorkerUpdateView {
	state: UpdateState;
	applyUpdate(): void;
}

/**
 * Mirror the arbiter's `UpdateState` into React.
 *
 * `applyUpdate()` is a *request*, never an override: it delegates to
 * `requestApplyUpdate()`, which re-consults the same policy the automatic path
 * uses, so the "never reload a dirty form" invariant holds even when the user
 * presses the button.
 *
 * `subscribeUpdateState` returns its own unsubscribe, which is what React calls
 * on unmount — the store must never retain a listener for an unmounted tree.
 */
export function useServiceWorkerUpdate(): ServiceWorkerUpdateView {
	const state = useSyncExternalStore(
		subscribeUpdateState,
		getUpdateState,
		getUpdateState,
	);

	return { state, applyUpdate: requestApplyUpdate };
}
